/**
 * The ONE gate every login deep link (`?next=`) goes through — billing-comms
 * Teil 2a.
 *
 * `next` comes from a URL anybody can craft: a mail link, a bookmark, a
 * phishing page. The cockpit writes it (PrivateRoute, the 401/MFA paths, the
 * error screen's "log in again") and reads it after login / MFA — and every
 * read and write goes through `safeNext`, which:
 *
 *  - accepts only a string of at most 2 KB that starts with exactly one "/"
 *    and carries no backslash and no control character;
 *  - parses it with `new URL(raw, origin)` and requires the SAME origin (so
 *    `https://evil…`, `//evil…`, `javascript:` and friends are out);
 *  - checks the decoded, lower-cased, dot-segment-resolved pathname — so
 *    `/AUTH/login`, `/%61uth/login`, `/user/../auth/login`, `/%2F%2Fevil` and
 *    an encoded backslash/control character cannot slip past the string
 *    checks; an undecodable escape (URIError) is a rejection, never a throw;
 *  - refuses the auth flow (`/auth/*`, `/login`, `/mfa-verify` — a loop or a
 *    dead end) and `/portal/*` (that route hard-navigates to the portal host);
 *  - returns the PARSED `pathname + search + hash`, never the raw string.
 *
 * `null` means "no usable next" — the caller lands on "/".
 */

const MAX_NEXT_LENGTH = 2048;

/** A backslash, a C0 control character (tab, CR, LF, NUL, …) or DEL. */
function hasUnsafeChar(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f || code === 0x5c) return true;
  }
  return false;
}

/** Routes a login may never land on. Matched as the path itself or a prefix ending in "/". */
const BLOCKED_PATHS = ["/auth", "/login", "/mfa-verify", "/portal"];

function isBlockedPath(decodedLowerPath: string): boolean {
  return BLOCKED_PATHS.some((p) => decodedLowerPath === p || decodedLowerPath.startsWith(`${p}/`));
}

function pageOrigin(): string {
  return typeof window !== "undefined" ? window.location.origin : "";
}

export function safeNext(raw: unknown, origin: string = pageOrigin()): string | null {
  if (typeof raw !== "string" || raw.length === 0 || raw.length > MAX_NEXT_LENGTH) return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  if (hasUnsafeChar(raw)) return null;

  let base: URL;
  let url: URL;
  try {
    base = new URL(origin);
    url = new URL(raw, base);
  } catch {
    return null;
  }
  if (url.origin !== base.origin) return null;

  let decoded: string;
  try {
    decoded = decodeURIComponent(url.pathname).toLowerCase();
  } catch {
    // URIError: an escape that does not decode — nothing honest to land on.
    return null;
  }
  if (hasUnsafeChar(decoded) || decoded.startsWith("//")) return null;
  if (isBlockedPath(decoded)) return null;

  return `${url.pathname}${url.search}${url.hash}`;
}

/** The validated `next` of a search string (`location.search`), or null. */
export function nextFromSearch(search: string): string | null {
  return safeNext(new URLSearchParams(search).get("next"));
}

/**
 * The explicit "sign in with another account" marker (billing-comms fix r1,
 * X1): `intent=switch` next to `next`. The login/MFA forms ignore `next` when a
 * DIFFERENT account signs in (it was written for the previous one) — except
 * when the visitor chose the switch on purpose, e.g. from a billing mail link
 * for another company (`CompanyMismatchNotice`). Only this exact value counts,
 * and it never widens `safeNext`: a refused `next` stays refused.
 */
export const SWITCH_ACCOUNT_INTENT = "switch";

/** Does the URL carry the exact switch marker? (`getAll`: one value, exactly "switch".) */
export function isSwitchAccountIntent(params: URLSearchParams): boolean {
  const values = params.getAll("intent");
  return values.length === 1 && values[0] === SWITCH_ACCOUNT_INTENT;
}

/**
 * `path` carrying `next` — validated again here, so a writer can never emit
 * what a reader would refuse. The start page is not worth carrying.
 * `switchAccount` adds the marker — only beside a `next` it applies to.
 */
export function withNext(
  path: string,
  next: string | null | undefined,
  opts?: { switchAccount?: boolean },
): string {
  const safe = safeNext(next);
  if (!safe || safe === "/") return path;
  const marker = opts?.switchAccount === true ? `&intent=${SWITCH_ACCOUNT_INTENT}` : "";
  return `${path}?next=${encodeURIComponent(safe)}${marker}`;
}

/** Where the browser is right now, as a `next` — for hard navigations outside React Router. */
export function currentLocationNext(): string | null {
  if (typeof window === "undefined") return null;
  const { pathname, search, hash } = window.location;
  return safeNext(`${pathname}${search}${hash}`);
}
